import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { BoxIcon } from 'lucide-react';
export interface Feature {
  title: string;
  description: string;
  icon: BoxIcon;
}
interface FeatureGridProps {
  title: string;
  subtitle?: string;
  features: Feature[];
}
export function FeatureGrid({
  title,
  subtitle,
  features
}: FeatureGridProps) {
  return <section className="py-24 bg-gradient-to-b from-white via-slate-50/30 to-white lg:py-40 relative overflow-hidden" data-id="element-109">
      {/* Decorative Background Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-20" data-id="element-110"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-20" data-id="element-111"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-112">
        <div className="mx-auto max-w-4xl text-center mb-12 sm:mb-16 lg:mb-20" data-id="element-113">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold text-blue-700 mb-4 sm:mb-6 border border-blue-100" data-id="element-114">
            <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" data-id="element-115"></div>
            <span data-id="element-116">Deine Vorteile</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6 sm:mb-8 leading-tight px-4" data-id="element-117">
            {title}
          </h2>
          {subtitle && <p className="text-lg sm:text-xl md:text-2xl text-slate-600 leading-relaxed max-w-3xl mx-auto px-4" data-id="element-118">
              {subtitle}
            </p>}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto" data-id="element-119">
          {features.map((feature, index) => <Card key={index} className="group relative border-slate-100 bg-white/80 backdrop-blur-sm shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden" data-id="element-120">
              {/* Gradient Overlay on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 via-blue-50/0 to-indigo-50/0 group-hover:from-blue-50/50 group-hover:via-blue-50/30 group-hover:to-indigo-50/50 transition-all duration-500 pointer-events-none" data-id="element-121"></div>

              {/* Top Border Accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" data-id="element-122"></div>

              <CardHeader className="relative z-10" data-id="element-123">
                <div className="mb-4 sm:mb-6 relative" data-id="element-124">
                  {/* Icon Glow Effect */}
                  <div className="absolute inset-0 bg-blue-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500" data-id="element-125"></div>
                  <div className="relative inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:text-white transition-all duration-500 shadow-md group-hover:shadow-xl group-hover:scale-110" data-id="element-126">
                    <feature.icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2} data-id="element-127" />
                  </div>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold group-hover:text-blue-700 transition-colors duration-300" data-id="element-128">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10" data-id="element-129">
                <CardDescription className="text-sm sm:text-base leading-relaxed text-slate-600" data-id="element-130">
                  {feature.description}
                </CardDescription>
              </CardContent>

              {/* Bottom Corner Decoration */}
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-blue-50/50 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-131"></div>
            </Card>)}
        </div>

        {/* Bottom Decorative Line */}
        <div className="mt-12 sm:mt-16 lg:mt-20 flex justify-center" data-id="element-132">
          <div className="h-1 w-24 sm:w-32 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full" data-id="element-133"></div>
        </div>
      </div>
    </section>;
}